import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icons } from '../../shared/ui';
const { Plus: UserPlusIcon, Edit: PencilIcon, Trash: TrashIcon, Refresh: ArrowPathIcon, X: XMarkIcon, CheckCircle2: CheckCircleIcon, XCircle: XCircleIcon, Copy: DocumentDuplicateIcon, Download: ArrowDownTrayIcon, Upload: ArrowUpTrayIcon, Mail: EnvelopeIcon, Lock: KeyIcon, Eye: EyeIcon, Users: UserGroupIcon } = Icons;
import axios from 'axios';
import toast from 'react-hot-toast';
import UserDetailModal from './UserDetailModal';

const UserManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openBulkDialog, setOpenBulkDialog] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetail, setShowUserDetail] = useState(false);

  // Form state for new user (admin/manager only - renters use Renters page)
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    role: 'manager',
    first_name: '',
    last_name: '',
    phone: '',
    send_invitation: true,
    set_custom_password: false,
    password: '',
    confirm_password: ''
  });

  // Bulk users CSV
  const [bulkUsers, setBulkUsers] = useState('');
  const [invitationResult, setInvitationResult] = useState(null);

  
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/users', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    // Validate password if custom password is set
    if (newUser.set_custom_password) {
      if (!newUser.password) {
        toast.error('Password is required when setting custom password');
        return;
      }
      if (newUser.password !== newUser.confirm_password) {
        toast.error('Passwords do not match');
        return;
      }
      if (newUser.password.length < 8) {
        toast.error('Password must be at least 8 characters long');
        return;
      }
    }

    try {
      setLoading(true);

      // Prepare request data
      const requestData = {
        username: newUser.username,
        email: newUser.email,
        role: newUser.role,
        first_name: newUser.first_name,
        last_name: newUser.last_name,
        phone: newUser.phone,
        send_invitation: newUser.send_invitation
      };

      // Add password only if custom password is set
      if (newUser.set_custom_password && newUser.password) {
        requestData.password = newUser.password;
      }

      const response = await axios.post('/api/users/create', requestData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      setInvitationResult(response.data);
      toast.success('User created successfully');

      // Reset form
      setNewUser({
        username: '',
        email: '',
        role: 'manager',
        first_name: '',
        last_name: '',
        phone: '',
        send_invitation: true,
        set_custom_password: false,
        password: '',
        confirm_password: ''
      });

      fetchUsers(); // Refresh user list
      setOpenDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkCreate = async () => {
    try {
      setLoading(true);

      // Parse CSV
      const lines = bulkUsers.trim().split('\n');
      const users = lines.map(line => {
        const [username, email, role, first_name, last_name] = line.split(',').map(s => s.trim());
        return { username, email, role: role || 'renter', first_name, last_name };
      });

      const response = await axios.post('/api/users/create-bulk',
        { users, send_invitations: true },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      toast.success(`Created ${response.data.successful} users successfully`);
      setOpenBulkDialog(false);
      setBulkUsers('');
      fetchUsers();
    } catch (error) {
      toast.error('Bulk creation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateUser = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) return;

    try {
      await axios.post(`/api/users/${userId}/deactivate`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('User deactivated');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to deactivate user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to PERMANENTLY DELETE this user? This action cannot be undone!')) return;

    try {
      await axios.delete(`/api/users/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('User permanently deleted');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleReactivateUser = async (userId) => {
    try {
      await axios.post(`/api/users/${userId}/reactivate`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      toast.success('User reactivated successfully. They can login with their existing password.');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to reactivate user');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
    setShowUserDetail(true);
  };

  const handleUserUpdate = (updatedUser) => {
    setUsers(prevUsers =>
      prevUsers.map(u => u.id === updatedUser.id ? updatedUser : u)
    );
    fetchUsers(); // Refresh the list to ensure consistency
  };

  const downloadTemplate = () => {
    const template = 'username,email,role,first_name,last_name\njohn_doe,john@example.com,renter,John,Doe\njane_smith,jane@example.com,manager,Jane,Smith';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'user_import_template.csv';
    a.click();
  };

  const tabs = ['All Users', 'Create User', 'Bulk Import', 'Activity Log'];

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-stone-900 mb-6">User Management</h1>

      {/* Tabs */}
      <div className="border-b border-stone-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab, index) => (
            <button
              key={tab}
              onClick={() => setSelectedTab(index)}
              className={`
                py-2 px-1 border-b-2 font-medium text-sm
                ${selectedTab === index
                  ? 'border-teal-500 text-teal-600'
                  : 'border-transparent text-stone-500 hover:text-stone-700 hover:border-stone-300'
                }
              `}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white shadow rounded-lg">
        {/* All Users Tab */}
        {selectedTab === 0 && (
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">All Users</h2>
              <button
                onClick={fetchUsers}
                className="flex items-center gap-2 px-4 py-2 bg-stone-100 hover:bg-stone-200 rounded-md"
              >
                <ArrowPathIcon size={16} />
                Refresh
              </button>
            </div>

            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-stone-200">
                  <thead className="bg-stone-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                        Username
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-stone-200">
                    {users.map((user) => (
                      <tr
                        key={user.id}
                        className={`cursor-pointer transition-colors ${
                          user.is_active
                            ? 'hover:bg-stone-50'
                            : 'bg-stone-50 opacity-60 hover:opacity-80'
                        }`}
                        onClick={() => handleUserClick(user)}
                      >
                        <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${
                          user.is_active ? 'text-stone-900' : 'text-stone-500'
                        }`}>
                          {user.username}
                          {!user.is_active && (
                            <span className="ml-2 text-xs text-red-500">(Deactivated)</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-500">
                          {user.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-500">
                          {`${user.first_name || ''} ${user.last_name || ''}`}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full
                            ${user.role === 'admin' ? 'bg-red-100 text-red-800' :
                              user.role === 'manager' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-stone-100 text-stone-800'}`}>
                            {user.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full
                            ${user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleUserClick(user)}
                              className="text-teal-600 hover:text-teal-900"
                              title="View Details"
                            >
                              <EyeIcon className="h-5 w-5" />
                            </button>
                            {user.is_active ? (
                              <button
                                onClick={() => handleDeactivateUser(user.id)}
                                className="text-yellow-600 hover:text-yellow-900"
                                title="Deactivate"
                              >
                                <XCircleIcon className="h-5 w-5" />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleReactivateUser(user.id)}
                                className="text-green-600 hover:text-green-900 font-bold"
                                title="Reactivate User"
                              >
                                <CheckCircleIcon className="h-5 w-5" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteUser(user.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete Permanently"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Create User Tab */}
        {selectedTab === 1 && (
          <div className="p-6">
            <h2 className="text-lg font-semibold mb-4">Create Admin/Manager User</h2>

            {/* Renter Creation Notice */}
            <div className="mb-6 bg-teal-50 rounded-xl p-4 border border-teal-200">
              <div className="flex items-center gap-3">
                <UserGroupIcon size={24} className="text-teal-600" />
                <div className="flex-1">
                  <h3 className="font-semibold text-stone-900">Need to create a Renter?</h3>
                  <p className="text-sm text-stone-600">Use the Renters page with ID Card OCR support for creating renter accounts.</p>
                </div>
                <button
                  onClick={() => navigate('/manager/renters')}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm font-medium"
                >
                  Go to Renters
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Username
                </label>
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  value={newUser.first_name}
                  onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  value={newUser.last_name}
                  onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Role
                </label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                >
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
                <p className="text-xs text-stone-500 mt-1">For renters, use the Renters page</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Phone
                </label>
                <input
                  type="text"
                  value={newUser.phone}
                  onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                />
              </div>

              <div className="col-span-2 space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newUser.send_invitation}
                    onChange={(e) => setNewUser({ ...newUser, send_invitation: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm text-stone-700">Send invitation email</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newUser.set_custom_password}
                    onChange={(e) => setNewUser({
                      ...newUser,
                      set_custom_password: e.target.checked,
                      password: '',
                      confirm_password: ''
                    })}
                    className="mr-2"
                  />
                  <span className="text-sm text-stone-700">Set custom password (instead of auto-generating)</span>
                </label>
              </div>

              {/* Password fields - only shown when set_custom_password is true */}
              {newUser.set_custom_password && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type="password"
                        value={newUser.password}
                        onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                        className="w-full px-3 py-2 pl-10 border border-stone-300 rounded-md focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                        placeholder="Minimum 8 characters"
                      />
                      <KeyIcon size={20} className="text-stone-400 absolute left-3 top-2.5" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <input
                        type="password"
                        value={newUser.confirm_password}
                        onChange={(e) => setNewUser({ ...newUser, confirm_password: e.target.value })}
                        className="w-full px-3 py-2 pl-10 border border-stone-300 rounded-md focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                        placeholder="Re-enter password"
                      />
                      <KeyIcon size={20} className="text-stone-400 absolute left-3 top-2.5" />
                    </div>
                    {newUser.password && newUser.confirm_password && newUser.password !== newUser.confirm_password && (
                      <p className="text-red-600 text-sm mt-1">Passwords do not match</p>
                    )}
                  </div>
                </>
              )}

              <div className="col-span-2">
                <button
                  onClick={handleCreateUser}
                  disabled={loading || !newUser.username || !newUser.email}
                  className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <UserPlusIcon className="h-5 w-5" />
                  Create User
                </button>
              </div>
            </div>

            {/* Show invitation result */}
            {invitationResult && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-md">
                <h3 className="text-sm font-medium text-green-800 mb-2">
                  User created successfully!
                </h3>
                <div className="space-y-2">
                  {invitationResult.custom_password_set ? (
                    <div className="text-sm text-stone-700">
                      <strong>Custom password has been set.</strong> The user can log in immediately with the password you provided.
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-stone-700">
                          <strong>Temporary Password:</strong> {invitationResult.temporary_password}
                        </span>
                        <button
                          onClick={() => copyToClipboard(invitationResult.temporary_password)}
                          className="text-teal-600 hover:text-teal-800"
                        >
                          <DocumentDuplicateIcon size={16} />
                        </button>
                      </div>
                      {invitationResult.invitation_link && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-stone-700">
                            <strong>Reset Link:</strong> {invitationResult.invitation_link.substring(0, 50)}...
                          </span>
                          <button
                            onClick={() => copyToClipboard(invitationResult.invitation_link)}
                            className="text-teal-600 hover:text-teal-800"
                          >
                            <DocumentDuplicateIcon size={16} />
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Bulk Import Tab */}
        {selectedTab === 2 && (
          <div className="p-6">
            <h2 className="text-lg font-semibold mb-4">Bulk Import Users</h2>
            <p className="text-sm text-stone-600 mb-4">
              Upload a CSV file with user data to create multiple users at once.
            </p>

            <button
              onClick={downloadTemplate}
              className="flex items-center gap-2 px-4 py-2 bg-stone-100 hover:bg-stone-200 rounded-md mb-4"
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              Download Template
            </button>

            <textarea
              className="w-full h-64 px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-teal-500 focus:border-teal-500"
              placeholder="username,email,role,first_name,last_name"
              value={bulkUsers}
              onChange={(e) => setBulkUsers(e.target.value)}
            />

            <button
              onClick={handleBulkCreate}
              disabled={loading || !bulkUsers}
              className="mt-4 flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowUpTrayIcon className="h-5 w-5" />
              Import Users
            </button>
          </div>
        )}

        {/* Activity Log Tab */}
        {selectedTab === 3 && (
          <div className="p-6">
            <h2 className="text-lg font-semibold mb-4">Activity Log</h2>
            <p className="text-stone-500">Activity log feature coming soon...</p>
          </div>
        )}
      </div>

      {/* User Detail Modal */}
      <UserDetailModal
        user={selectedUser}
        isOpen={showUserDetail}
        onClose={() => {
          setShowUserDetail(false);
          setSelectedUser(null);
        }}
        onUpdate={handleUserUpdate}
      />
    </div>
  );
};

export default UserManagement;